/**
 * SEO Route Corridor Data
 *
 * Generates city-to-city shipping route pages with route-specific content.
 * Europe ↔ Africa corridors (bidirectional). The Africa side is config-driven:
 * adding a newly supported African country means adding one entry to
 * AFRICA_SIDE below — no generator code changes. Only countries that are
 * operationally supported may be listed; the configured set currently includes Morocco and Tunisia.
 *
 * SCALE: European cities × configured Africa-side cities × 2 directions
 */

export interface SEORoute {
  slug: string;
  fromCity: string;
  fromCountry: string;
  fromFlag: string;
  fromIso: string;
  toCity: string;
  toCountry: string;
  toFlag: string;
  toIso: string;
  /** Approximate distance in km */
  distanceKm: number;
  /** Typical transit days range */
  transitDays: [number, number];
  /** Relevant border crossing or port */
  borderCrossing: string;
  /** SEO priority tier: 1 = high-volume, 2 = medium, 3 = low/niche */
  tier: 1 | 2 | 3;
}

/**
 * Future-ready live data interface.
 */
export interface RouteLiveData {
  courierCount?: number;
  tripsThisWeek?: number;
  avgDeliveryDays?: number;
  bookingsLast30Days?: number;
  lastUpdated?: string;
}

// Approximate distances from European cities to Casablanca (km, as-the-crow-flies × 1.3 road factor)
const DISTANCE_TO_CASA: Record<string, number> = {
  // France
  Paris: 2170, Lyon: 1950, Marseille: 1700, Bordeaux: 1850, Toulouse: 1750,
  Lille: 2350, Strasbourg: 2350, Nantes: 2100, Montpellier: 1650, Nice: 1850,
  Rennes: 2200, Grenoble: 1950,
  // Belgium
  Brussels: 2400, Antwerp: 2450, Liège: 2400, Charleroi: 2380,
  // Netherlands
  Amsterdam: 2600, Rotterdam: 2550, Utrecht: 2580, "The Hague": 2580, Eindhoven: 2500,
  // Germany
  Berlin: 3100, Frankfurt: 2700, Cologne: 2600, Munich: 2800,
  Hamburg: 3200, Düsseldorf: 2620, Stuttgart: 2650, Dortmund: 2650,
  // Spain
  Madrid: 850, Barcelona: 1350, Valencia: 1200, Seville: 500, Malaga: 450,
  Alicante: 1100, Zaragoza: 1150, Bilbao: 1400,
  // UK
  London: 2500, Manchester: 2700, Birmingham: 2650,
  // Italy
  Milan: 2300, Rome: 2500, Turin: 2150, Naples: 2700, Bologna: 2350,
  // Portugal
  Lisbon: 700, Porto: 950,
  // Scandinavia
  Copenhagen: 3300, Stockholm: 3800, Gothenburg: 3500, Malmö: 3250,
  Oslo: 3700, Helsinki: 4200,
  // Central/Eastern Europe
  Vienna: 3000, Dublin: 2900, Warsaw: 3600,
  Zurich: 2400, Geneva: 2200, Basel: 2350,
  Budapest: 3200, Bucharest: 3600,
};

// Offsets from Casablanca to other Moroccan cities (km)
const MA_OFFSET: Record<string, number> = {
  Casablanca: 0, Rabat: -90, Tangier: -300, Marrakech: 240,
  Fes: -180, Agadir: 460, Nador: -100, Oujda: 50,
  Meknes: -140, Kenitra: -120, Tetouan: -280, "Beni Mellal": 180,
};

// Border crossing relevant for the route
export const BORDER_CROSSINGS: Record<string, string> = {
  France: "Tangier Med port (ferry via Algeciras or Sète–Tangier)",
  Belgium: "Tangier Med port (via France & Spain)",
  Netherlands: "Tangier Med port (via France & Spain)",
  Germany: "Tangier Med port (via France & Spain)",
  Spain: "Algeciras–Tangier Med ferry (36 km strait crossing)",
  "United Kingdom": "Channel Tunnel then Tangier Med port",
  Italy: "Genoa–Tangier Med ferry or overland via Spain",
  Portugal: "Algeciras–Tangier Med ferry (via southern Spain)",
  Denmark: "Tangier Med port (overland via Germany, France & Spain)",
  Sweden: "Tangier Med port (overland via Denmark, Germany & France)",
  Austria: "Tangier Med port (via Germany, France & Spain)",
  Ireland: "Ferry to France, then Tangier Med port",
  Poland: "Tangier Med port (via Germany, France & Spain)",
  Switzerland: "Tangier Med port (via France & Spain)",
  Norway: "Tangier Med port (overland via Sweden, Denmark & Germany)",
  Finland: "Tangier Med port (overland via Sweden, Denmark & Germany)",
  Hungary: "Tangier Med port (via Austria, Germany & France)",
  Romania: "Tangier Med port (via Hungary, Austria & France)",
};

// Tier 1: France (largest Moroccan diaspora) + Spain/Belgium/Netherlands top cities
const TIER1_CITIES = new Set([
  "Paris", "Lyon", "Marseille", "Bordeaux", "Toulouse", "Lille",
  "Brussels", "Antwerp", "Amsterdam", "Rotterdam",
  "Madrid", "Barcelona", "Seville", "Malaga",
  "Strasbourg", "Montpellier", "Nice",
]);

// Tier 2: other major hubs with meaningful diaspora
const TIER2_CITIES = new Set([
  "London", "Milan", "Rome", "Turin", "Berlin", "Frankfurt", "Cologne", "Munich",
  "Utrecht", "Lisbon", "Geneva", "Zurich", "Valencia", "Nantes",
  "Hamburg", "Düsseldorf", "Stuttgart", "Liège", "Charleroi",
  "The Hague", "Eindhoven", "Naples", "Bologna", "Porto",
  "Birmingham", "Manchester", "Copenhagen", "Stockholm",
  "Vienna", "Dublin", "Warsaw",
]);

// Top Moroccan cities by search demand
const TIER1_MA = new Set(["Casablanca", "Tangier", "Rabat", "Marrakech", "Nador", "Oujda", "Fes", "Agadir"]);

// Moroccan cities for SEO pages — expanded to 12
export const MOROCCO_CITIES = [
  { city: "Casablanca", flag: "🇲🇦", iso: "MA" },
  { city: "Rabat", flag: "🇲🇦", iso: "MA" },
  { city: "Tangier", flag: "🇲🇦", iso: "MA" },
  { city: "Marrakech", flag: "🇲🇦", iso: "MA" },
  { city: "Fes", flag: "🇲🇦", iso: "MA" },
  { city: "Agadir", flag: "🇲🇦", iso: "MA" },
  { city: "Nador", flag: "🇲🇦", iso: "MA" },
  { city: "Oujda", flag: "🇲🇦", iso: "MA" },
  { city: "Meknes", flag: "🇲🇦", iso: "MA" },
  { city: "Kenitra", flag: "🇲🇦", iso: "MA" },
  { city: "Tetouan", flag: "🇲🇦", iso: "MA" },
  { city: "Beni Mellal", flag: "🇲🇦", iso: "MA" },
];

// Top Tunisian cities by search demand
const TIER1_TN = new Set(["Tunis", "Sfax", "Sousse", "Djerba", "Monastir", "Bizerte", "Nabeul"]);

// Tunisian cities for SEO pages — 20
export const TUNISIA_CITIES = [
  { city: "Tunis", flag: "🇹🇳", iso: "TN" },
  { city: "Sfax", flag: "🇹🇳", iso: "TN" },
  { city: "Sousse", flag: "🇹🇳", iso: "TN" },
  { city: "Kairouan", flag: "🇹🇳", iso: "TN" },
  { city: "Bizerte", flag: "🇹🇳", iso: "TN" },
  { city: "Gabès", flag: "🇹🇳", iso: "TN" },
  { city: "Ariana", flag: "🇹🇳", iso: "TN" },
  { city: "Gafsa", flag: "🇹🇳", iso: "TN" },
  { city: "Monastir", flag: "🇹🇳", iso: "TN" },
  { city: "Nabeul", flag: "🇹🇳", iso: "TN" },
  { city: "Djerba", flag: "🇹🇳", iso: "TN" },
  { city: "Ben Arous", flag: "🇹🇳", iso: "TN" },
  { city: "La Marsa", flag: "🇹🇳", iso: "TN" },
  { city: "Médenine", flag: "🇹🇳", iso: "TN" },
  { city: "Béja", flag: "🇹🇳", iso: "TN" },
  { city: "Jendouba", flag: "🇹🇳", iso: "TN" },
  { city: "Zarzis", flag: "🇹🇳", iso: "TN" },
  { city: "Hammamet", flag: "🇹🇳", iso: "TN" },
  { city: "Kasserine", flag: "🇹🇳", iso: "TN" },
  { city: "Sidi Bouzid", flag: "🇹🇳", iso: "TN" },
];

// Approximate combined road + ferry distance from European cities to Tunis (km)
const DISTANCE_TO_TUNIS: Record<string, number> = {
  // France
  Paris: 1700, Lyon: 1400, Marseille: 1000, Bordeaux: 1750, Toulouse: 1550,
  Lille: 1950, Strasbourg: 1750, Nantes: 1900, Montpellier: 1200, Nice: 1100,
  Rennes: 1950, Grenoble: 1300,
  // Belgium
  Brussels: 2000, Antwerp: 2050, Liège: 1950, Charleroi: 1980,
  // Netherlands
  Amsterdam: 2200, Rotterdam: 2150, Utrecht: 2180, "The Hague": 2180, Eindhoven: 2100,
  // Germany
  Berlin: 2500, Frankfurt: 2100, Cologne: 2150, Munich: 1900,
  Hamburg: 2600, Düsseldorf: 2200, Stuttgart: 1950, Dortmund: 2250,
  // Spain
  Madrid: 2000, Barcelona: 1400, Valencia: 1650, Seville: 2300, Malaga: 2150,
  Alicante: 1700, Zaragoza: 1650, Bilbao: 2100,
  // UK
  London: 2300, Manchester: 2600, Birmingham: 2450,
  // Italy
  Milan: 1350, Rome: 900, Turin: 1450, Naples: 800, Bologna: 1150,
  // Portugal
  Lisbon: 2700, Porto: 2800,
  // Scandinavia
  Copenhagen: 2900, Stockholm: 3400, Gothenburg: 3100, Malmö: 2850,
  Oslo: 3300, Helsinki: 3800,
  // Central/Eastern Europe
  Vienna: 2100, Dublin: 2900, Warsaw: 2800,
  Zurich: 1700, Geneva: 1500, Basel: 1750,
  Budapest: 2250, Bucharest: 2700,
};

// Offsets from Tunis to other Tunisian cities (km)
const TN_OFFSET: Record<string, number> = {
  Tunis: 0, Ariana: 10, "Ben Arous": 10, "La Marsa": 20,
  Bizerte: 65, Nabeul: 65, Hammamet: 65, Béja: 105,
  Sousse: 145, Jendouba: 155, Kairouan: 160, Monastir: 165,
  Sfax: 270, "Sidi Bouzid": 270, Kasserine: 300, Gafsa: 350,
  Gabès: 400, Djerba: 510, Médenine: 510, Zarzis: 560,
};

// Border crossing / ferry relevant for each European origin country
const TN_BORDER_CROSSINGS: Record<string, string> = {
  France: "Marseille–Tunis ferry (CTN / Corsica Linea)",
  Belgium: "Marseille–Tunis ferry (overland via France)",
  Netherlands: "Marseille or Genoa–Tunis ferry (overland via France)",
  Germany: "Genoa–Tunis ferry (overland via Switzerland & Italy)",
  Spain: "Marseille–Tunis ferry (overland via southern France)",
  "United Kingdom": "Channel Tunnel, then Marseille–Tunis ferry",
  Italy: "Genoa, Civitavecchia or Salerno–Tunis ferry",
  Portugal: "Marseille–Tunis ferry (overland via Spain & France)",
  Denmark: "Genoa–Tunis ferry (overland via Germany & Italy)",
  Sweden: "Genoa–Tunis ferry (overland via Denmark, Germany & Italy)",
  Austria: "Genoa–Tunis ferry (overland via Italy)",
  Ireland: "Ferry to France, then Marseille–Tunis ferry",
  Poland: "Genoa–Tunis ferry (overland via Austria & Italy)",
  Switzerland: "Genoa–Tunis ferry (overland via Italy)",
  Norway: "Genoa–Tunis ferry (overland via Denmark, Germany & Italy)",
  Finland: "Genoa–Tunis ferry (overland via Sweden, Germany & Italy)",
  Hungary: "Genoa–Tunis ferry (overland via Austria & Italy)",
  Romania: "Genoa–Tunis ferry (overland via Hungary, Austria & Italy)",
};

/**
 * Africa-side country configuration. Each entry fully describes one supported
 * African country: its cities, distance anchors, border crossings and demand
 * tiers. Adding a newly supported country = adding one entry here.
 * Only operationally supported countries may be listed — never add a country
 * ahead of its corridors going live.
 */
export interface AfricaSideCountry {
  country: string;
  iso: string;
  flag: string;
  cities: readonly { city: string; flag: string; iso: string }[];
  tier1Cities: ReadonlySet<string>;
  /**
   * Cities shown in marketing navigation (footer, route directory). A curated
   * subset — roughly ten per country — so the visible site never grows a wall
   * of links as the SEO city list expands. This says NOTHING about whether the
   * country is live on the marketplace; it is a navigation-curation rule only.
   */
  marketingCities: readonly string[];
  /** Approximate road distance (km) from each European city to the anchor city */
  anchorDistances: Record<string, number>;
  /** Distance offsets (km) from the anchor city to each listed city */
  cityDistanceOffsets: Record<string, number>;
  /** Border crossing / port description per European origin country */
  borderCrossings: Record<string, string>;
  defaultBorderCrossing: string;
}

export const AFRICA_SIDE: readonly AfricaSideCountry[] = [
  {
    country: "Morocco",
    iso: "MA",
    flag: "🇲🇦",
    cities: MOROCCO_CITIES,
    tier1Cities: TIER1_MA,
    marketingCities: [
      "Casablanca", "Tangier", "Rabat", "Marrakech", "Fes",
      "Agadir", "Nador", "Oujda", "Meknes", "Tetouan",
    ],
    anchorDistances: DISTANCE_TO_CASA,
    cityDistanceOffsets: MA_OFFSET,
    borderCrossings: BORDER_CROSSINGS,
    defaultBorderCrossing: "Tangier Med port",
  },
  {
    country: "Tunisia",
    iso: "TN",
    flag: "🇹🇳",
    cities: TUNISIA_CITIES,
    tier1Cities: TIER1_TN,
    marketingCities: [
      "Tunis", "Sfax", "Sousse", "Bizerte", "Monastir",
      "Nabeul", "Djerba", "Kairouan", "Gabès", "Ariana",
    ],
    anchorDistances: DISTANCE_TO_TUNIS,
    cityDistanceOffsets: TN_OFFSET,
    borderCrossings: TN_BORDER_CROSSINGS,
    defaultBorderCrossing: "Tunis La Goulette port (ferry from Marseille or Genoa)",
  },
];

/** Names of Africa-side countries currently described by the config */
export const AFRICA_COUNTRY_NAMES: ReadonlySet<string> = new Set(AFRICA_SIDE.map(c => c.country));

/** Curated marketing cities for an Africa-side country (footer / directory). */
export function getMarketingCities(country: string): readonly string[] {
  return AFRICA_SIDE.find(c => c.country === country)?.marketingCities ?? [];
}

/** Every curated marketing city across all Africa-side countries. */
export const MARKETING_CITY_NAMES: ReadonlySet<string> = new Set(
  AFRICA_SIDE.flatMap(c => c.marketingCities),
);

/** True when a city may appear in marketing navigation. */
export function isMarketingCity(city: string): boolean {
  return MARKETING_CITY_NAMES.has(city);
}

/** Test seam: exposes the number of generated country hubs via seoHubs (avoids import cycle) */
export function getCountryHubCountForTest(): number {
  const euCountries = new Set(EUROPE_CITIES.map(c => c.country)).size;
  return euCountries * AFRICA_SIDE.length * 2;
}

// European cities — expanded to 55
export const EUROPE_CITIES = [
  // France (12)
  { city: "Paris", country: "France", flag: "🇫🇷", iso: "FR" },
  { city: "Lyon", country: "France", flag: "🇫🇷", iso: "FR" },
  { city: "Marseille", country: "France", flag: "🇫🇷", iso: "FR" },
  { city: "Bordeaux", country: "France", flag: "🇫🇷", iso: "FR" },
  { city: "Toulouse", country: "France", flag: "🇫🇷", iso: "FR" },
  { city: "Lille", country: "France", flag: "🇫🇷", iso: "FR" },
  { city: "Strasbourg", country: "France", flag: "🇫🇷", iso: "FR" },
  { city: "Nantes", country: "France", flag: "🇫🇷", iso: "FR" },
  { city: "Montpellier", country: "France", flag: "🇫🇷", iso: "FR" },
  { city: "Nice", country: "France", flag: "🇫🇷", iso: "FR" },
  { city: "Rennes", country: "France", flag: "🇫🇷", iso: "FR" },
  { city: "Grenoble", country: "France", flag: "🇫🇷", iso: "FR" },
  // Belgium (4)
  { city: "Brussels", country: "Belgium", flag: "🇧🇪", iso: "BE" },
  { city: "Antwerp", country: "Belgium", flag: "🇧🇪", iso: "BE" },
  { city: "Liège", country: "Belgium", flag: "🇧🇪", iso: "BE" },
  { city: "Charleroi", country: "Belgium", flag: "🇧🇪", iso: "BE" },
  // Netherlands (5)
  { city: "Amsterdam", country: "Netherlands", flag: "🇳🇱", iso: "NL" },
  { city: "Rotterdam", country: "Netherlands", flag: "🇳🇱", iso: "NL" },
  { city: "Utrecht", country: "Netherlands", flag: "🇳🇱", iso: "NL" },
  { city: "The Hague", country: "Netherlands", flag: "🇳🇱", iso: "NL" },
  { city: "Eindhoven", country: "Netherlands", flag: "🇳🇱", iso: "NL" },
  // Germany (8)
  { city: "Berlin", country: "Germany", flag: "🇩🇪", iso: "DE" },
  { city: "Frankfurt", country: "Germany", flag: "🇩🇪", iso: "DE" },
  { city: "Cologne", country: "Germany", flag: "🇩🇪", iso: "DE" },
  { city: "Munich", country: "Germany", flag: "🇩🇪", iso: "DE" },
  { city: "Hamburg", country: "Germany", flag: "🇩🇪", iso: "DE" },
  { city: "Düsseldorf", country: "Germany", flag: "🇩🇪", iso: "DE" },
  { city: "Stuttgart", country: "Germany", flag: "🇩🇪", iso: "DE" },
  { city: "Dortmund", country: "Germany", flag: "🇩🇪", iso: "DE" },
  // Spain (8)
  { city: "Madrid", country: "Spain", flag: "🇪🇸", iso: "ES" },
  { city: "Barcelona", country: "Spain", flag: "🇪🇸", iso: "ES" },
  { city: "Valencia", country: "Spain", flag: "🇪🇸", iso: "ES" },
  { city: "Seville", country: "Spain", flag: "🇪🇸", iso: "ES" },
  { city: "Malaga", country: "Spain", flag: "🇪🇸", iso: "ES" },
  { city: "Alicante", country: "Spain", flag: "🇪🇸", iso: "ES" },
  { city: "Zaragoza", country: "Spain", flag: "🇪🇸", iso: "ES" },
  { city: "Bilbao", country: "Spain", flag: "🇪🇸", iso: "ES" },
  // UK (3)
  { city: "London", country: "United Kingdom", flag: "🇬🇧", iso: "GB" },
  { city: "Manchester", country: "United Kingdom", flag: "🇬🇧", iso: "GB" },
  { city: "Birmingham", country: "United Kingdom", flag: "🇬🇧", iso: "GB" },
  // Italy (5)
  { city: "Milan", country: "Italy", flag: "🇮🇹", iso: "IT" },
  { city: "Rome", country: "Italy", flag: "🇮🇹", iso: "IT" },
  { city: "Turin", country: "Italy", flag: "🇮🇹", iso: "IT" },
  { city: "Naples", country: "Italy", flag: "🇮🇹", iso: "IT" },
  { city: "Bologna", country: "Italy", flag: "🇮🇹", iso: "IT" },
  // Portugal (2)
  { city: "Lisbon", country: "Portugal", flag: "🇵🇹", iso: "PT" },
  { city: "Porto", country: "Portugal", flag: "🇵🇹", iso: "PT" },
  // Scandinavia (5)
  { city: "Copenhagen", country: "Denmark", flag: "🇩🇰", iso: "DK" },
  { city: "Stockholm", country: "Sweden", flag: "🇸🇪", iso: "SE" },
  { city: "Gothenburg", country: "Sweden", flag: "🇸🇪", iso: "SE" },
  { city: "Malmö", country: "Sweden", flag: "🇸🇪", iso: "SE" },
  { city: "Oslo", country: "Norway", flag: "🇳🇴", iso: "NO" },
  // Other (8)
  { city: "Helsinki", country: "Finland", flag: "🇫🇮", iso: "FI" },
  { city: "Vienna", country: "Austria", flag: "🇦🇹", iso: "AT" },
  { city: "Dublin", country: "Ireland", flag: "🇮🇪", iso: "IE" },
  { city: "Warsaw", country: "Poland", flag: "🇵🇱", iso: "PL" },
  { city: "Zurich", country: "Switzerland", flag: "🇨🇭", iso: "CH" },
  { city: "Geneva", country: "Switzerland", flag: "🇨🇭", iso: "CH" },
  { city: "Basel", country: "Switzerland", flag: "🇨🇭", iso: "CH" },
];

function toSlug(text: string): string {
  return text.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
}

function computeDistance(euCity: string, afCity: string, side: AfricaSideCountry): number {
  const base = side.anchorDistances[euCity] ?? 2500;
  const offset = side.cityDistanceOffsets[afCity] ?? 0;
  return Math.round(base + offset);
}

function computeTransitDays(distanceKm: number): [number, number] {
  if (distanceKm <= 800) return [1, 3];
  if (distanceKm <= 1200) return [2, 4];
  if (distanceKm <= 2000) return [3, 5];
  if (distanceKm <= 3000) return [4, 6];
  return [5, 8];
}

function computeTier(euCity: string, afCity: string, side: AfricaSideCountry): 1 | 2 | 3 {
  const isHighEU = TIER1_CITIES.has(euCity);
  const isMidEU = TIER2_CITIES.has(euCity);
  const isHighAF = side.tier1Cities.has(afCity);

  if (isHighEU && isHighAF) return 1;
  if ((isHighEU || isMidEU) && isHighAF) return 2;
  if (isHighEU) return 2;
  return 3;
}

/** Memoized route list */
let _cachedRoutes: SEORoute[] | null = null;

/**
 * Pure generator: bidirectional city-to-city routes for any list of
 * Africa-side country configs. Exported so a new country can be validated
 * (and tested) without touching the live AFRICA_SIDE list.
 */
export function generateRoutesForSide(side: readonly AfricaSideCountry[]): SEORoute[] {
  const routes: SEORoute[] = [];

  for (const af of side) {
    for (const eu of EUROPE_CITIES) {
      for (const afCity of af.cities) {
        const distanceKm = computeDistance(eu.city, afCity.city, af);
        const transitDays = computeTransitDays(distanceKm);
        const borderCrossing = af.borderCrossings[eu.country] ?? af.defaultBorderCrossing;
        const tier = computeTier(eu.city, afCity.city, af);

        // Europe → Africa
        routes.push({
          slug: `${toSlug(eu.city)}-to-${toSlug(afCity.city)}`,
          fromCity: eu.city, fromCountry: eu.country, fromFlag: eu.flag, fromIso: eu.iso,
          toCity: afCity.city, toCountry: af.country, toFlag: afCity.flag, toIso: afCity.iso,
          distanceKm, transitDays, borderCrossing, tier,
        });
        // Africa → Europe
        routes.push({
          slug: `${toSlug(afCity.city)}-to-${toSlug(eu.city)}`,
          fromCity: afCity.city, fromCountry: af.country, fromFlag: afCity.flag, fromIso: afCity.iso,
          toCity: eu.city, toCountry: eu.country, toFlag: eu.flag, toIso: eu.iso,
          distanceKm, transitDays, borderCrossing, tier,
        });
      }
    }
  }

  return routes;
}

/** Generate all bidirectional city-to-city SEO routes (memoized) */
export function generateSEORoutes(): SEORoute[] {
  if (_cachedRoutes) return _cachedRoutes;
  _cachedRoutes = generateRoutesForSide(AFRICA_SIDE);
  return _cachedRoutes;
}

/**
 * Get routes for the public directory page, grouped by European country.
 * Marketing surface: only curated marketing cities are listed, so the visible
 * directory stays readable however many cities the SEO config carries.
 */
export function getTopRoutesByCountry(): Record<string, SEORoute[]> {
  const all = generateSEORoutes();
  const grouped: Record<string, SEORoute[]> = {};

  for (const route of all) {
    const country = AFRICA_COUNTRY_NAMES.has(route.fromCountry) ? route.toCountry : route.fromCountry;
    if (!grouped[country]) grouped[country] = [];
    // Only keep Europe→Africa direction for directory to avoid duplicates
    if (!AFRICA_COUNTRY_NAMES.has(route.fromCountry) && isMarketingCity(route.toCity)) {
      grouped[country].push(route);
    }
  }

  return grouped;
}

/** Slug → route index for O(1) lookup (memoized) */
let _slugIndex: Map<string, SEORoute> | null = null;
function getSlugIndex(): Map<string, SEORoute> {
  if (_slugIndex) return _slugIndex;
  _slugIndex = new Map(generateSEORoutes().map(r => [r.slug, r]));
  return _slugIndex;
}

/** Find a route by slug (O(1)) */
export function findRouteBySlug(slug: string): SEORoute | undefined {
  return getSlugIndex().get(slug);
}

/** Get popular items for SEO content */
export const POPULAR_ITEMS = [
  "washing machines", "fridges", "ovens", "TVs", "furniture",
  "beds", "tables", "suitcases", "boxes", "electronics",
  "bicycles", "scooters", "Moroccan olive oil", "personal belongings",
];

/** Get related routes (same origin country or same destination) */
export function getRelatedRoutes(slug: string, limit = 6): SEORoute[] {
  const current = findRouteBySlug(slug);
  if (!current) return [];
  
  return generateSEORoutes()
    .filter(r => r.slug !== slug && (
      (r.fromCountry === current.fromCountry && r.fromCity !== current.fromCity) ||
      (r.toCity === current.toCity && r.fromCountry !== current.fromCountry)
    ))
    .slice(0, limit);
}

/** Get all routes for sitemap (all tiers — every page must be indexable) */
export function getSitemapRoutes(): SEORoute[] {
  return generateSEORoutes();
}

/** Get the reverse route (B → A when given A → B) */
export function getReverseRoute(slug: string): SEORoute | undefined {
  const current = findRouteBySlug(slug);
  if (!current) return undefined;
  const reverseSlug = `${toSlug(current.toCity)}-to-${toSlug(current.fromCity)}`;
  return findRouteBySlug(reverseSlug);
}

/** Get other routes departing from the same city */
export function getRoutesFromCity(city: string, excludeSlug: string, limit = 6): SEORoute[] {
  return generateSEORoutes()
    .filter(r => r.slug !== excludeSlug && r.fromCity === city)
    .sort((a, b) => a.tier - b.tier)
    .slice(0, limit);
}

/** Get other routes arriving at the same destination */
export function getRoutesToCity(city: string, excludeSlug: string, limit = 6): SEORoute[] {
  return generateSEORoutes()
    .filter(r => r.slug !== excludeSlug && r.toCity === city)
    .sort((a, b) => a.tier - b.tier)
    .slice(0, limit);
}

/**
 * Top routes for marketing navigation (footer, directory, guides).
 * Tier 1 only and restricted to curated marketing cities.
 */
export function getTopGlobalRoutes(limit = 12): SEORoute[] {
  return generateSEORoutes()
    .filter(r => r.tier === 1 && !AFRICA_COUNTRY_NAMES.has(r.fromCountry) && isMarketingCity(r.toCity))
    .slice(0, limit);
}
